import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { generateLeadsFromLinkedin, getLeads, generateLeads, deleteLead, setLeadsAsPreferred, setLeadsAsNonPreferred } from "../services/gutcheck-admin-api.service";
import { TextField, Stack, Button, Box, TableCell, TableRow, TableHead, TableBody, Checkbox } from "@mui/material";
import { ILead, ICompany, IProfile, IAdvisor } from "../../../types"; 
import { AdvisorTable } from "../../company/styles/AdvisorStyles";
import dayjs from "dayjs";
import { AdvisorProfileImage } from "../../advisor";
import LinkedInIcon from '@mui/icons-material/LinkedIn';

export const AdminProjectLeads = () => {
    const { project } = useOutletContext<any>();
    const [ linkedInLeads, setLinkedInLeads ] = useState<string>("");
    const [ isGeneratingLeads, setIsGeneratingLeads ] = useState<boolean>(false);
    const [ leads, setLeads ] = useState<ILead[]>([]);
    const [ error, setError ] = useState<string>("");
    const [ addLeads, setAddLeads ] = useState<boolean>(false);
    const [ message, setMessage ] = useState<string>("");
    const [ selectedLeads, setSelectedLeads ] = useState<string[]>([]);
    const { getAccessTokenSilently } = useAuth0();

    useEffect(() => {
        const fetchLeads = async () => {
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await getLeads(project.id, accessToken);
            if (error) {
                setError(error.message);
            } else {
                setLeads(data?.leads ?? []);
            }
        }
        fetchLeads();
    }, []);

    const handleAddLeads = async () => {
        setMessage("");
        const accessToken = await getAccessTokenSilently();
        setIsGeneratingLeads(true);
        const { data, error } = await generateLeadsFromLinkedin(project.id, linkedInLeads, accessToken);
        if (error) {
            setError(error.message);    
            setMessage("");
        } else {
            setMessage("Leads are processing. Refresh the page to see new leads.");
        }
        setIsGeneratingLeads(false);
        setLinkedInLeads("");
        setAddLeads(false);
    }

    const handleGenerateLeads = async () => {
        setMessage("");
        const accessToken = await getAccessTokenSilently();
        setIsGeneratingLeads(true);
        const { data, error } = await generateLeads(project.id, accessToken);
        if (error) {
            setError(error.message);
        } else {
            setMessage("Leads are processing. Refresh the page to see new leads.");
        }
        setIsGeneratingLeads(false);
    }

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedLeads(leads.map(lead => lead.uuid));
        } else {
            setSelectedLeads([]);
        }
    };

    const handleSelectLead = (leadUuid: string) => {
        setMessage("");
        setSelectedLeads(prev => 
            prev.includes(leadUuid) 
                ? prev.filter(id => id !== leadUuid)
                : [...prev, leadUuid]
        );
    };

    const handleBulkAction = async (action: 'delete' | 'preferred' | 'nonPreferred') => {
        if (selectedLeads.length === 0) return;
        
        const accessToken = await getAccessTokenSilently();
        setError("");
        setMessage("");

        try {
            switch (action) {
                case 'delete':
                    await deleteLead(project.id, selectedLeads, accessToken);
                    setMessage("Selected leads have been deleted.");
                    break;
                case 'preferred':
                    await setLeadsAsPreferred(project.id, selectedLeads, accessToken);
                    setMessage("Selected leads have been marked as preferred.");
                    break;
                case 'nonPreferred':
                    await setLeadsAsNonPreferred(project.id, selectedLeads, accessToken);
                    setMessage("Selected leads have been marked as non-preferred.");
                    break;
            }
            
            // Refresh leads after action
            const { data, error } = await getLeads(project.id, accessToken);
            if (error) {
                setError(error.message);
            } else {
                setLeads(data?.leads ?? []);
                setSelectedLeads([]);
            }
        } catch (err) {
            setError("An error occurred while processing the request.");
        }
    };

    return (
        <div>
            <Stack spacing={2} direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <h5>Leads</h5>
                <Stack spacing={2} direction="row" justifyContent="flex-end" alignItems="center">
                    { message && <p>{message}</p> }
                    { selectedLeads.length > 0 && (
                        <>
                            <Button 
                                variant="outlined" 
                                color="error" 
                                onClick={() => handleBulkAction('delete')}
                            >
                                Delete Selected
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                onClick={() => handleBulkAction('preferred')}
                            >
                                Mark as Preferred
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                onClick={() => handleBulkAction('nonPreferred')}
                            >
                                Mark as Non-Preferred
                            </Button>
                        </>
                    )}
                    <Button variant="contained" onClick={handleGenerateLeads} disabled={isGeneratingLeads}>{isGeneratingLeads ? "Processing..." : "Auto Generate Leads"}</Button>
                    <Button variant="contained" onClick={() => { setAddLeads(true); setMessage("");}} disabled={isGeneratingLeads}>{isGeneratingLeads ? "Processing..." : "Add Leads"}</Button>
                </Stack>
            </Stack>
            {addLeads && (
            <Stack spacing={2} className="display-box">
                <p>Enter the LinkedIn URLs you want to generate leads for (1 per line).</p>
                <TextField rows={10} multiline fullWidth value={linkedInLeads} onChange={(e) => setLinkedInLeads(e.target.value)} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, alignItems: "center", width: "100%" }}>
                    {error && <p style={{ color: "red" }}>{error}</p>}
                    <Button variant="outlined" onClick={() => setAddLeads(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddLeads} disabled={isGeneratingLeads}>{isGeneratingLeads ? "Processing..." : "Add Leads"}</Button>
                </Box>
            </Stack>
            )}
            <AdvisorTable>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Checkbox 
                                checked={selectedLeads.length === leads.length && leads.length > 0}
                                indeterminate={selectedLeads.length > 0 && selectedLeads.length < leads.length}
                                onChange={handleSelectAll}
                            />
                        </TableCell>
                        <TableCell>Date Added</TableCell>
                        <TableCell>ID</TableCell>
                        <TableCell>Image</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Job Title</TableCell>
                        <TableCell>Is Preferred</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    { leads.map((lead: ILead) => {
                        const advisor = {
                            uuid: lead.uuid,
                            user: {
                                uuid: lead.uuid as string,
                                profile: {
                                    name: lead.name as string,
                                    title: lead.title as string,
                                    company_name: lead.company_name as string,
                                    image_url: lead.image_url as string,
                                    linkedin_url: lead.linkedin_url as string,
                                    company: {
                                        name: lead.company_name as string,
                                    } as ICompany
                                } as IProfile
                            }
                        } as IAdvisor;
                        
                        return (
                        <TableRow key={lead.uuid}>
                            <TableCell>
                                <Checkbox 
                                    checked={selectedLeads.includes(lead.uuid)}
                                    onChange={() => handleSelectLead(lead.uuid)}
                                />
                            </TableCell>
                            <TableCell>{dayjs(lead.created_at).format("MM/DD/YYYY")}</TableCell>
                            <TableCell>{lead.id}</TableCell>
                            <TableCell>
                                <AdvisorProfileImage advisor={advisor} size="small"/>
                            </TableCell>
                            <TableCell>{lead.name}</TableCell>
                            <TableCell>{lead.company_name}</TableCell>
                            <TableCell>{lead.title}</TableCell>
                            <TableCell>{lead.is_preferred ? "Yes" : "No"}</TableCell>
                            <TableCell>
                            </TableCell>
                        </TableRow>
                        )
                    })}
                </TableBody>
            </AdvisorTable>

            
        </div>

        

    )
}